import { getNameLabel } from '../utils/autocompleteLabelUtils';

export type ContactDisplayItem = {
    name?:
        | string
        | {
              firstName?: string | null;
              lastName?: string | null;
          }
        | null;
    personReference?: {
        id?: string | null;
        label?: string | null;
    } | null;
    organization?: string | null;
    organizationReference?: {
        label?: string | null;
    } | null;
    role?: string | null;
};

export type ContactRoleGroup<T extends ContactDisplayItem = ContactDisplayItem> = {
    role: string;
    contacts: T[];
};

export const DEFAULT_CONTACT_ROLE = 'Unspecified';

export const getContactDisplayName = (contact: ContactDisplayItem): string => {
    const referenceLabel = contact?.personReference?.label?.trim();
    if (referenceLabel) {
        return referenceLabel;
    }

    return getNameLabel(contact?.name).trim();
};

export const getContactOrganizationName = (contact: ContactDisplayItem): string =>
    contact?.organizationReference?.label?.trim() || contact?.organization?.trim() || '';

export const formatContactLine = (contact: ContactDisplayItem): string => {
    const contactName = getContactDisplayName(contact);
    const organizationName = getContactOrganizationName(contact);

    if (!contactName) {
        return organizationName ? `(${organizationName})` : '';
    }

    return organizationName ? `${contactName} (${organizationName})` : contactName;
};

export const groupContactsByRole = <T extends ContactDisplayItem>(
    contacts: Array<T | null | undefined> = [],
    defaultContactRole: string = DEFAULT_CONTACT_ROLE
): ContactRoleGroup<T>[] => {
    const fallbackRole = defaultContactRole.trim() || DEFAULT_CONTACT_ROLE;
    const groupsByRole = contacts.reduce<Map<string, T[]>>((groups, contact) => {
        if (!contact) {
            return groups;
        }

        const role = contact.role?.trim() || fallbackRole;
        const group = groups.get(role) || [];
        group.push(contact);
        groups.set(role, group);
        return groups;
    }, new Map<string, T[]>());

    return Array.from(groupsByRole.entries())
        .sort(([leftRole], [rightRole]) => leftRole.localeCompare(rightRole))
        .map(([role, contactsForRole]) => ({
            role,
            contacts: contactsForRole
                .map((contact, index) => ({
                    contact,
                    index,
                    name: getContactDisplayName(contact),
                    organizationName: getContactOrganizationName(contact)
                }))
                .sort((left, right) => {
                    const nameComparison = left.name.localeCompare(right.name);
                    if (nameComparison !== 0) {
                        return nameComparison;
                    }

                    const organizationComparison = left.organizationName.localeCompare(right.organizationName);
                    if (organizationComparison !== 0) {
                        return organizationComparison;
                    }

                    return left.index - right.index;
                })
                .map(({ contact }) => contact)
        }));
};
